// ═══════════════════════════════════════════════════════════════
// job-helpers — shared formatters, time math, and channel openers
// used across Jobs, Outstanding, History, and the Leave modal.
// Pulled out of Jobs.jsx so siblings can reuse without circular
// imports back into the Jobs screen.
// ═══════════════════════════════════════════════════════════════

// ─── Money ──────────────────────────────────────────────────────
export function formatPrice(n) {
  if (n == null || isNaN(n)) return '$0.00';
  return `$${Number(n).toFixed(2)}`;
}

// ─── Line items (parts + labor) ─────────────────────────────────
// Job shape (post 2026-05-13 parts/labor split):
//   {
//     description,
//     lineItems: [
//       { id, type: 'labor', description, amount },
//       { id, type: 'part',  description, qty, unitPrice },
//       ...
//     ],
//     shopSupplies,    // optional flat $ amount
//     totalPrice       // auto-computed sum (kept for backward compat
//                      // with card displays, public pages, stats)
//   }
//
// Hourly-rate labor (future "Option C") will add optional `hours` +
// `hourlyRate` fields to labor items. lineItemAmount() will prefer
// hours*hourlyRate when both are set; falls back to `amount` otherwise.
// That keeps today's data forward-compatible.
export function lineItemAmount(li) {
  if (!li) return 0;
  if (li.type === 'labor') {
    if (Number.isFinite(li.hours) && Number.isFinite(li.hourlyRate)) {
      return Number(li.hours) * Number(li.hourlyRate);
    }
    return Number(li.amount) || 0;
  }
  if (li.type === 'part') {
    return (Number(li.qty) || 0) * (Number(li.unitPrice) || 0);
  }
  return 0;
}

export function computeLaborSubtotal(items) {
  return (items || [])
    .filter((li) => li.type === 'labor')
    .reduce((s, li) => s + lineItemAmount(li), 0);
}

export function computePartsSubtotal(items) {
  return (items || [])
    .filter((li) => li.type === 'part')
    .reduce((s, li) => s + lineItemAmount(li), 0);
}

// The PRE-TAX total. Deliberately still called totalPrice and
// deliberately still tax-free — see computeJobTax below.
export function computeJobTotal({ lineItems, shopSupplies }) {
  return (
    computeLaborSubtotal(lineItems) +
    computePartsSubtotal(lineItems) +
    (Number(shopSupplies) || 0)
  );
}

// ─── Sales tax ──────────────────────────────────────────────────
//
// TAX IS NOT REVENUE. It is money collected on behalf of a tax
// authority and owed to them. That single fact decides the whole
// shape of this:
//
//   totalPrice  stays the pre-tax subtotal, unchanged, forever.
//   taxTotal    is what was collected on top.
//   grandTotal  is what the customer actually pays.
//
// Rolling tax into totalPrice would have been fewer lines of code and
// would have silently restated every earnings figure in the app —
// Earnings, the average-ticket number, the CSV export and the stats
// on Home all read totalPrice, and all of them mean "what I made".
// Adding the state's money to that would overstate income on a tax
// return. So tax travels in its own fields and every existing job,
// which has none, keeps exactly the totals it has today.
//
// The job's stored `tax` block:
//   { label, partsRate, laborRate, taxSupplies }
// Absent or all-zero means no tax, which is every job created before
// this shipped.

// Money rounds to cents ONCE, per bucket, at the point it becomes a
// number a customer reads. Parts and labor are rounded separately
// because they appear as separate lines on the invoice — rounding the
// sum instead can leave the two printed lines disagreeing with the
// printed total by a cent, which is the kind of thing that makes
// someone distrust the whole document.
export function roundCents(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return 0;
  return Math.round(v * 100) / 100;
}

function rateOf(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(n, 100);
}

// Returns every component, not just the total, because the customer's
// invoice itemizes them and the mechanic's export needs them apart
// for filing.
export function computeJobTax({ lineItems, shopSupplies, tax }) {
  const partsRate = rateOf(tax?.partsRate);
  const laborRate = rateOf(tax?.laborRate);
  // Supplies are goods in most jurisdictions, so they follow the
  // PARTS rate — never the labor rate, and never a rate of their own.
  const taxSupplies = tax?.taxSupplies !== false;

  const laborBase = computeLaborSubtotal(lineItems);
  const partsBase = computePartsSubtotal(lineItems);
  const suppliesBase = taxSupplies ? (Number(shopSupplies) || 0) : 0;

  const laborTax = roundCents(laborBase * (laborRate / 100));
  const partsTax = roundCents(partsBase * (partsRate / 100));
  const suppliesTax = roundCents(suppliesBase * (partsRate / 100));

  return {
    partsRate,
    laborRate,
    taxSupplies,
    laborBase,
    partsBase,
    suppliesBase,
    laborTax,
    partsTax,
    suppliesTax,
    // Parts and supplies share a rate, so they read as one line on the
    // invoice: the customer sees "Sales tax on parts", not two.
    partsAndSuppliesTax: roundCents(partsTax + suppliesTax),
    total: roundCents(laborTax + partsTax + suppliesTax),
  };
}

// Is there a tax block worth rendering? Rates of zero are a real
// answer (this county taxes neither) but shouldn't print $0.00 lines.
export function hasTax(source) {
  return computeJobTax(source || {}).total > 0;
}

// What the customer pays. Falls back to the pre-tax total for every
// job and every share doc created before tax existed.
export function computeGrandTotal({ lineItems, shopSupplies, tax }) {
  return roundCents(
    computeJobTotal({ lineItems, shopSupplies }) +
    computeJobTax({ lineItems, shopSupplies, tax }).total
  );
}

// Normalize whatever is on a job into the stored tax block, or null
// when there's no tax on this job. Null rather than a zeroed object so
// a job with no tax stores nothing at all and stays byte-identical to
// how it looks today.
export function normalizeJobTax(tax) {
  if (!tax) return null;
  const partsRate = rateOf(tax.partsRate);
  const laborRate = rateOf(tax.laborRate);
  if (partsRate === 0 && laborRate === 0) return null;
  return {
    label: (tax.label || '').trim().slice(0, 60),
    partsRate,
    laborRate,
    taxSupplies: tax.taxSupplies !== false,
    // Which preset this came from, if any — so a later rename of the
    // preset doesn't have to hunt down the jobs that used it, and so
    // "where did this rate come from" has an answer.
    sourcePresetId: tax.sourcePresetId || '',
  };
}

// Legacy line items used { type: 'labor', qty: 1, unitPrice: total }.
// Normalize to the new shape (labor uses `amount`) so the editor
// component doesn't have to handle both formats. Pure function — won't
// mutate the input.
export function normalizeLineItem(li) {
  if (!li || !li.type) return li;
  if (li.type === 'labor') {
    // Already migrated
    if (li.amount != null || li.hours != null) return li;
    // Legacy: unitPrice carried the labor amount
    return {
      id: li.id || shortId(),
      type: 'labor',
      description: li.description || '',
      amount: Number(li.unitPrice) || 0,
    };
  }
  if (li.type === 'part') {
    return {
      id: li.id || shortId(),
      type: 'part',
      description: li.description || '',
      qty: Number(li.qty) || 1,
      unitPrice: Number(li.unitPrice) || 0,
    };
  }
  return li;
}

export function normalizeLineItems(items) {
  if (!Array.isArray(items)) return [];
  return items.map(normalizeLineItem);
}

// Blank-row factories for the editor
export function blankLaborItem() {
  return { id: shortId(), type: 'labor', description: '', amount: 0 };
}
export function blankPartItem() {
  return { id: shortId(), type: 'part', description: '', qty: 1, unitPrice: 0 };
}

// ─── Phone display ──────────────────────────────────────────────
export function formatPhone(raw) {
  const d = String(raw || '').replace(/\D/g, '');
  if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
  if (d.length === 11 && d[0] === '1') return `(${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`;
  return raw || '';
}

// ─── Random unguessable IDs (share tokens, return-visit IDs) ────
export function randomToken() {
  const a = new Uint8Array(16);
  crypto.getRandomValues(a);
  return Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('');
}

// Shorter ID for in-array entries like return visits — we don't need
// 32 hex chars for that, 12 is plenty unique.
export function shortId() {
  const a = new Uint8Array(6);
  crypto.getRandomValues(a);
  return Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('');
}

// ─── Firestore Timestamp / Date / null → Date | null ────────────
export function toDate(v) {
  if (!v) return null;
  if (v.toDate) return v.toDate();
  if (v instanceof Date) return v;
  return new Date(v);
}

// Whole days between a Firestore Timestamp (or Date) and now (floored,
// never negative). Returns null if the timestamp is missing.
export function daysSince(ts) {
  const d = toDate(ts);
  if (!d) return null;
  const ms = Date.now() - d.getTime();
  if (ms < 0) return 0;
  return Math.floor(ms / (24 * 60 * 60 * 1000));
}

export function daysAgoLabel(days, prefix) {
  if (days == null) return null;
  if (days === 0) return `${prefix} today`;
  if (days === 1) return `${prefix} yesterday`;
  return `${prefix} ${days} days ago`;
}

// ─── Channel openers ────────────────────────────────────────────
// Both rely on the device's URL scheme handlers. mailto: opens the
// default mail client, sms: opens Messages / SMS app.
export function openSms(phone, body) {
  const phoneDigits = String(phone || '').replace(/\D/g, '');
  const href = phoneDigits
    ? `sms:${phoneDigits}?body=${encodeURIComponent(body)}`
    : `sms:?body=${encodeURIComponent(body)}`;
  window.location.href = href;
}

export function openMailto(email, subject, body) {
  const href = `mailto:${encodeURIComponent(email)}`
    + `?subject=${encodeURIComponent(subject)}`
    + `&body=${encodeURIComponent(body)}`;
  window.location.href = href;
}

// ─── Status taxonomy (single source of truth) ───────────────────
//
// STATUS_ORDER is the forward-only ladder a healthy job climbs.
// 'cancelled' is deliberately NOT on it: it's a terminal state a job
// drops into sideways from anywhere, not a rung. Keeping it off the
// ladder keeps the status filter chips and the progress logic honest.
export const STATUS_ORDER = ['draft', 'sent', 'approved', 'invoiced', 'paid'];
export const STATUS_CANCELLED = 'cancelled';
export const STATUS_TEXT = {
  draft:    'Draft',
  sent:     'Sent',
  approved: 'Approved',
  invoiced: 'Invoiced',
  paid:     'Paid',
  cancelled: 'Cancelled',
};

// Why a job died. The reason is worth more than the fact — six months
// on, "customer sold the car" and "went somewhere cheaper" lead to
// completely different conversations.
export const CANCEL_REASONS = [
  { value: 'customer-declined', label: 'Customer declined the work' },
  { value: 'vehicle-sold',      label: 'Vehicle sold or gone' },
  { value: 'went-elsewhere',    label: 'Went somewhere else' },
  { value: 'scheduling',        label: "Couldn't make scheduling work" },
  { value: 'mistake',           label: 'Created by mistake' },
  { value: 'other',             label: 'Other' },
];

export function cancelReasonLabel(value) {
  return CANCEL_REASONS.find((r) => r.value === value)?.label || 'Cancelled';
}

export function isCancelled(job) {
  return job?.status === STATUS_CANCELLED;
}

// ─── Customer archiving ─────────────────────────────────────────
// Deleting a customer used to wipe them, every vehicle and every job
// permanently, behind one confirm dialog and with no undo. Archiving
// is the same idea as cancelling a job: it disappears from everywhere
// you work, but the record survives and can be brought back.
//
// IMPORTANT: an archived customer is still a customer. Home decides
// which jobs are ORPHANS by checking whether their customerId still
// exists, and offers to bulk-delete the ones that don't. If archived
// customers were filtered out of that list, every archived customer's
// jobs would be offered up for permanent deletion — turning the undo
// feature into a worse version of the problem it replaces. So filter
// archived customers at the point of DISPLAY, never out of the set
// used for orphan detection.
export function isArchived(customer) {
  return !!customer?.archivedAt;
}

// The active customers — what every list, picker and dashboard should
// show. Orphan detection must NOT use this.
export function activeCustomers(customers) {
  return (Array.isArray(customers) ? customers : []).filter((c) => !isArchived(c));
}

export function archivedCustomers(customers) {
  return (Array.isArray(customers) ? customers : []).filter(isArchived);
}

// Jobs belonging to customers who are still active. Used by Home and
// the Calendar so an archived customer's work stops appearing without
// being deleted.
export function jobsForActiveCustomers(jobs, customers) {
  const archived = new Set(archivedCustomers(customers).map((c) => c.id));
  return (Array.isArray(jobs) ? jobs : []).filter((j) => !archived.has(j.customerId));
}

// ─── Job-state predicates ───────────────────────────────────────
// "Fully closed" = work is done AND money is in. Lives in History,
// hidden from Home and per-customer active lists.
export function isFullyClosed(job) {
  return !!job?.closedAt && job?.status === 'paid';
}

// Done with, one way or the other — either the money came in or the
// job was cancelled. Everything that lists ACTIVE work filters on
// this, so a cancelled job stops appearing on Home, the calendar, the
// customer's job list, and the follow-up nags.
export function isTerminal(job) {
  return isFullyClosed(job) || isCancelled(job);
}

// Closed but not yet paid — surfaces on Outstanding via status='invoiced'
// for the typical case, but we also want closed-without-invoice deals
// (cash on the spot but not marked paid yet) to surface somewhere.
export function isClosedNeedsPayment(job) {
  return !isCancelled(job) && !!job?.closedAt && job?.status !== 'paid';
}

// Currently in progress = Brad has tapped Start but hasn't tapped Leave.
export function isRunning(job) {
  return !isCancelled(job) && !!job?.startedAt && !job?.closedAt && !job?.awaitingReturn;
}

// Best landing page for a job tap — active workspace when the timer is running.
export function jobPrimaryPath(job) {
  if (!job?.id) return '/';
  if (isRunning(job)) return `/active/${job.id}`;
  if (job.customerId) return `/customers/${job.customerId}`;
  return '/';
}

// Does this job have a visit still ahead of it — either its original
// appointment or a return trip? Used to tell an approved job that's
// already on the calendar apart from one nobody has scheduled yet.
export function hasFutureVisit(job, now = new Date()) {
  const t = now.getTime();
  const initial = toDate(job?.scheduledAt);
  if (initial && initial.getTime() >= t) return true;
  return (Array.isArray(job?.returnVisits) ? job.returnVisits : []).some((v) => {
    const d = toDate(v?.scheduledAt);
    return d && d.getTime() >= t;
  });
}

// The gap this closes: a customer approves an estimate from their
// couch at 9pm and nothing lands on anyone's calendar. The job is
// approved, the work hasn't been done, and no visit is booked — so
// it needs a trip scheduled before it can go anywhere.
export function needsScheduling(job, now = new Date()) {
  if (job?.status !== 'approved') return false;   // cancelled never matches
  if (job?.closedAt) return false;
  if (isRunning(job)) return false;          // tech is at the car right now
  return !hasFutureVisit(job, now);
}

// Build a return-visit entry and the job patch that appends it.
// Shared by the Leave flow and by scheduling straight off an
// approval, so both write the same shape — the calendar reads
// returnVisits[] and doesn't care which screen created the entry.
export function appendReturnVisitPatch(job, { scheduledAt, durationMinutes, notes, waitingPart }) {
  const visit = {
    id: shortId(),
    scheduledAt,                    // plain Date — Firestore converts it
    durationMinutes,
    kind: waitingPart ? 'parts-arrival' : 'return',
    notes: (notes || '').trim(),
    addedAt: new Date(),
  };
  const existing = Array.isArray(job?.returnVisits) ? job.returnVisits : [];
  return {
    visit,
    patch: {
      returnVisits: [...existing, visit],
      awaitingReturn: true,
      awaitingPart: !!waitingPart,
      returnNotes: (notes || '').trim(),
    },
  };
}

// Waiting on a future return visit (Leave-not-complete state).
export function isAwaitingReturn(job) {
  return !isCancelled(job) && !!job?.awaitingReturn && !job?.closedAt;
}

// Flatten every job's scheduled visits into a single array of calendar
// blocks — one block per appointment OR return visit. Used by:
//   - Calendar.jsx for rendering
//   - JobLeaveModal.jsx for conflict-checking a proposed return visit
//   - any future cross-customer "what's on the calendar today" view
//
// Skips fully-closed jobs (they belong on History, not the calendar).
// Each block carries a `_visitKind` ('initial' | 'return' | 'parts-arrival')
// and a `_visitId` (null for initial visits) so callers can tell entries
// apart and exclude one-being-edited from conflict checks.
export function flatMapVisits(jobs, defaultDuration = 60) {
  const blocks = [];
  jobs.forEach((j) => {
    if (isTerminal(j)) return;
    const initial = toDate(j.scheduledAt);
    if (initial) {
      blocks.push({
        ...j,
        _blockKey: `${j.id}::initial`,
        _start: initial,
        _durationMinutes: j.durationMinutes || defaultDuration,
        _visitKind: 'initial',
        _visitId: null,
      });
    }
    if (Array.isArray(j.returnVisits)) {
      j.returnVisits.forEach((v) => {
        const start = toDate(v.scheduledAt);
        if (!start) return;
        blocks.push({
          ...j,
          _blockKey: `${j.id}::${v.id}`,
          _start: start,
          _durationMinutes: v.durationMinutes || defaultDuration,
          _visitKind: v.kind === 'parts-arrival' ? 'parts-arrival' : 'return',
          _visitId: v.id,
          _visitNotes: v.notes || '',
        });
      });
    }
  });
  return blocks;
}
